import { z } from 'zod';
import { AssistantVoiceCallSchema, AssistantVoiceCallType } from './voiceCall.js';
/** Whether the conversation was spoken or written. */
export var AssistantChatSessionModality;
(function (AssistantChatSessionModality) {
    AssistantChatSessionModality["Text"] = "TEXT";
    AssistantChatSessionModality["Voice"] = "VOICE";
})(AssistantChatSessionModality || (AssistantChatSessionModality = {}));
/** Who said a thing, as the model that produced the conversation labels it. */
export var AssistantChatMessageRole;
(function (AssistantChatMessageRole) {
    AssistantChatMessageRole["Human"] = "human";
    AssistantChatMessageRole["Ai"] = "ai";
    AssistantChatMessageRole["System"] = "system";
    AssistantChatMessageRole["Tool"] = "tool";
    AssistantChatMessageRole["ToolCallResult"] = "tool_call_result";
})(AssistantChatMessageRole || (AssistantChatMessageRole = {}));
/** The channel a message travelled over. */
export var AssistantChatMessageSource;
(function (AssistantChatMessageSource) {
    AssistantChatMessageSource["Web"] = "WEB";
    AssistantChatMessageSource["Sms"] = "SMS";
    AssistantChatMessageSource["Voice"] = "VOICE";
})(AssistantChatMessageSource || (AssistantChatMessageSource = {}));
/**
 * One turn of a conversation. On a voice call these are the transcribed utterances and the tool
 * calls the assistant made between them, in the order they happened.
 */
export const AssistantChatMessageSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    sessionId: z.uuid(),
    content: z
        .string()
        .nullable()
        .describe('What was said, or the tool result that stood in for it.'),
    role: z.enum(AssistantChatMessageRole).nullable(),
    source: z.enum(AssistantChatMessageSource).nullable(),
    intent: z.string().nullable(),
    component: z.string().nullable(),
    feedback: z.string().nullable(),
    comment: z.string().nullable(),
    entities: z.record(z.string(), z.unknown()).nullable(),
    toolCalls: z.array(z.record(z.string(), z.unknown())).nullable(),
    toolCallResponses: z.array(z.record(z.string(), z.unknown())).nullable(),
    userId: z.uuid().nullable().describe('The staff user who sent this message, when one did.'),
    ehrInformationId: z.uuid().nullable().describe('The patient this message is attributed to.'),
});
/** One conversation with an assistant, spoken or written, and where it came from. */
export const AssistantChatSessionSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    modality: z.enum(AssistantChatSessionModality).nullable(),
    isTest: z.boolean(),
    ehrInformationId: z.uuid().nullable().describe('The patient the conversation was with.'),
    encounterId: z.uuid().nullable(),
    userId: z.uuid().nullable(),
    flowDefinitionId: z.uuid().nullable().describe('The flow that placed or answered the call.'),
    flowRunId: z.uuid().nullable(),
    authenticatedTimestamp: z.iso
        .datetime()
        .nullable()
        .describe('When the caller proved who they were, if they ever did.'),
});
/**
 * One voice call with the conversation it produced: the session it belongs to, every message in
 * order, and the call's own telephony and outcome fields.
 */
export const AssistantVoiceCallConversationSchema = AssistantChatSessionSchema.extend({
    structuredOutputs: z
        .record(z.string(), z.unknown())
        .nullable()
        .describe('What the assistant was asked to extract from the conversation.'),
    voiceCall: AssistantVoiceCallSchema,
    messages: z.array(AssistantChatMessageSchema).describe('The transcript, in the requested order.'),
});
/**
 * One row of the conversation list: a session with the summary fields of the voice call it
 * carried, if it carried one. Read the call itself for its transcript, cost, and outcome, and
 * for the structured outputs it produced, which a page of rows has no room for.
 */
export const AssistantChatSessionListItemSchema = AssistantChatSessionSchema.extend({
    voiceCallId: z.uuid().nullable(),
    voiceCallCreated: z.iso.datetime().nullable(),
    voiceCallUpdated: z.iso.datetime().nullable(),
    voiceCallType: z.enum(AssistantVoiceCallType).nullable(),
    voiceCallStartTime: z.iso.datetime().nullable(),
    voiceCallEndTime: z.iso.datetime().nullable(),
    voiceCallEndedReason: z.string().nullable(),
    voiceCallToPhoneNumber: z.string().nullable(),
    voiceCallFromPhoneNumber: z.string().nullable(),
    voiceCallAssistantDefinitionId: z.uuid().nullable(),
    voiceCallAssistantId: z.uuid().nullable(),
    voiceCallAssistantName: z.string().nullable(),
    voiceCallPracticeId: z.uuid().nullable(),
});
/**
 * One page of conversations, and where to continue from.
 *
 * A full page is otherwise indistinguishable from the end of the results, which leaves a caller
 * paging until it sees a short one. The next offset answers that and nothing more: it is not a
 * count, which this list does not offer.
 */
export const AssistantChatSessionListResponseSchema = z.object({
    chatSessions: z.array(AssistantChatSessionListItemSchema),
    meta: z.object({
        page: z.object({
            nextOffset: z
                .number()
                .int()
                .nonnegative()
                .nullable()
                .describe('Offset that reads the next page, or null when this page is the end of the results.'),
        }),
    }),
});
//# sourceMappingURL=chatSession.js.map